import { useMemo } from 'react';

import { MultiFileDiff } from '@pierre/diffs/react';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';

export interface PromptDiffPrimaryAction {
  label: string;
  onClick: () => void;
  disabled?: boolean;
}

export interface PromptDiffDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  subtitle?: string;
  oldContent: string;
  newContent: string;
  /** Optional override for the file name shown by the diff (defaults to "prompt.md"). */
  fileName?: string;
  /** Label for the dismiss button (defaults to "Cerrar"). */
  cancelLabel?: string;
  /** Optional primary action rendered next to the dismiss button. */
  primaryAction?: PromptDiffPrimaryAction;
}

const PromptDiffDialog = ({
  open,
  onOpenChange,
  title,
  subtitle,
  oldContent,
  newContent,
  fileName = 'prompt.md',
  cancelLabel = 'Cerrar',
  primaryAction,
}: PromptDiffDialogProps): JSX.Element => {
  const oldFile = useMemo(() => ({ name: fileName, contents: oldContent }), [fileName, oldContent]);
  const newFile = useMemo(() => ({ name: fileName, contents: newContent }), [fileName, newContent]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="flex max-h-[90vh] w-[min(1100px,calc(100vw-2rem))] max-w-none flex-col gap-0 overflow-hidden p-0 sm:max-w-none">
        <DialogHeader className="border-b border-border-subtle px-6 py-4">
          <DialogTitle className="text-base font-semibold">{title}</DialogTitle>
          {subtitle && <DialogDescription className="text-xs text-text-muted">{subtitle}</DialogDescription>}
        </DialogHeader>
        <div className="flex min-h-0 flex-1 flex-col gap-2 overflow-auto p-6">
          <div className="text-xs font-semibold text-text-default">Contenido</div>
          {open && (
            <MultiFileDiff
              oldFile={oldFile}
              newFile={newFile}
              options={{
                theme: { dark: 'pierre-dark', light: 'pierre-light' },
                themeType: 'light',
                diffStyle: 'split',
                disableFileHeader: true,
                overflow: 'wrap',
              }}
            />
          )}
        </div>
        <DialogFooter className="m-0 gap-2 rounded-none border-t border-border-subtle bg-transparent px-6 py-4">
          <Button type="button" size="sm" variant="outline" onClick={() => onOpenChange(false)}>
            {cancelLabel}
          </Button>
          {primaryAction && (
            <Button
              type="button"
              size="sm"
              disabled={primaryAction.disabled}
              onClick={primaryAction.onClick}
              className="bg-gray-900 text-white hover:bg-gray-800"
            >
              {primaryAction.label}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default PromptDiffDialog;
